﻿import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, IconButton, Spinner, SpinnerSize } from "@fluentui/react";
import { toast } from "react-toastify";
import styles from "./CampaignChatMembers.module.css";
import { gloggedinuser, gselectedgroup, gcampaign, gcampaignchatmemberrow } from "../../interfaces";
import { qGetCampaignChatMembers } from "../../api/qpi";

// ───────────────────────────────────────────────────────────────────
// Chat-campaign member list.
// Member-centric view: one row per campaign contact, pre-filtered to the
// active campaign, showing collected data inline and the full conversation
// in a modal. Reuses existing chat reporting data only (no chat-flow change).
// ───────────────────────────────────────────────────────────────────

interface CampaignChatMembersProps {
    campaign: gcampaign;
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser;
    /** bump from the parent to force a re-fetch (e.g. after contacts change) */
    refreshKey?: number;
}

interface ConvoTurn {
    key: number;
    question: string;
    response: string;
    positive: number | null;
    feedback: string | null;
    convo_id: number | null;
    stamp: Date | null;
}

interface MemberGroup {
    target_id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
    collecteddata: Record<string, any>;
    turns: ConvoTurn[];
    /** filled in during memo parsing once turns are sorted */
    firstMessageAt?: Date | null;
    lastMessageAt?: Date | null;
    durationLabel?: string;
}

// minimal markdown-ish renderer, mirrors AnalyticsConvoList formatText so the
// chat-campaign conversation reads the same as the existing analytics view
const formatText = (content: string): JSX.Element[] => {
    const output: JSX.Element[] = [];
    const lines = (content || "").split("\\n");
    let listItems: JSX.Element[] = [];
    lines.forEach((line, index) => {
        if (line.startsWith("### ")) {
            if (listItems.length > 0) {
                output.push(<ul key={`ul-${index}`}>{listItems}</ul>);
                listItems = [];
            }
            output.push(<h3 key={index}>{line.substring(4)}</h3>);
        } else if (line.match(/^\d+\. /)) {
            listItems.push(<li key={index}>{line.substring(line.indexOf(" ") + 1)}</li>);
        } else if (line.includes("**")) {
            const parts = line.split("**").map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
            output.push(<p key={index}>{parts}</p>);
        } else if (line.trim() === "") {
            output.push(<br key={index} />);
        } else {
            output.push(<p key={index}>{line}</p>);
        }
    });
    if (listItems.length > 0) {
        output.push(<ul key="final-ul">{listItems}</ul>);
    }
    return output;
};

const renderVal = (v: any): string => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") {
        try { return JSON.stringify(v); } catch { return String(v); }
    }
    if (v == "undetermined"){
        return "Undetermined";
    }
    return String(v);
};

// Format a turn's timestamp for display. The stamp is derived from the chatlog
// `key` (YYYYMMDDHHmmss) in UTC, so render it in UTC to preserve the logged time.
const formatTurnStamp = (d?: Date | null): string => {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
};

// Human-readable elapsed time between the first and last message.
const formatDuration = (start?: Date | null, end?: Date | null): string => {
    if (!start || !end) return "";
    let ms = end.getTime() - start.getTime();
    if (isNaN(ms)) return "";
    if (ms < 0) ms = 0;
    const totalSec = Math.floor(ms / 1000);
    if (totalSec === 0) return "0s";
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (mins) parts.push(`${mins}m`);
    if (secs && !days && !hours) parts.push(`${secs}s`);
    return parts.join(" ") || "0s";
};

const CampaignChatMembers: React.FC<CampaignChatMembersProps> = ({ campaign, selectedGroup, loggedInUser, refreshKey }) => {
    const [rows, setRows]       = useState<gcampaignchatmemberrow[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeMember, setActiveMember] = useState<MemberGroup | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await qGetCampaignChatMembers(loggedInUser, campaign.campaign_id, selectedGroup.selectionId);
            setRows(data);
        } catch { toast.error("Failed to load campaign members"); }
        setLoading(false);
    }, [loggedInUser, campaign.campaign_id, selectedGroup.selectionId, refreshKey]);

    useEffect(() => { load() }, [load]);

    // group flat chatlog rows into one entry per member
    const members = useMemo<MemberGroup[]>(() => {
        const map = new Map<string, MemberGroup>();
        for (const r of rows) {
            const id = r.target_id;
            let m = map.get(id);
            if (!m) {
                m = {
                    target_id: id,
                    name: r.target_name || r.target_email || r.target_phone_number || id,
                    email: r.target_email || "",
                    phone: r.target_phone_number || "",
                    position: r.target_position || "",
                    collecteddata: {},
                    turns: []
                };
                map.set(id, m);
            }
            // merge collected data from any convo this member had
            if (r.collecteddata && typeof r.collecteddata === "object") {
                m.collecteddata = { ...m.collecteddata, ...r.collecteddata };
            }
            // a real chatlog turn has a key
            if (r.key !== null && r.key !== undefined) {
                m.turns.push({
                    key: r.key,
                    question: r.question || "",
                    response: r.response || "",
                    positive: r.positive ?? null,
                    feedback: r.feedback ?? null,
                    convo_id: r.convo_id ?? null,
                    stamp: r.stamp ?? null
                });
            }
        }
        const list = Array.from(map.values());
        list.forEach(m => {
            m.turns.sort((a, b) => (a.key || 0) - (b.key || 0));
            const stamps = m.turns
                .map(t => t.stamp)
                .filter((s): s is Date => s instanceof Date && !isNaN(s.getTime()));
            if (stamps.length > 0) {
                m.firstMessageAt = stamps[0];
                m.lastMessageAt = stamps[stamps.length - 1];
                m.durationLabel = formatDuration(m.firstMessageAt, m.lastMessageAt);
            }
        });
        return list;
    }, [rows]);

    if (loading) return <Spinner size={SpinnerSize.medium} label="Loading members…" />;

    if (members.length === 0) return <div className={styles.emptyState}>No members in this campaign yet. Add contacts, then send mail invites.</div>;


    return (
        <div className={styles.wrap}>
            <div className={styles.memberList}>
                {members.map(m => {
                    const collectedEntries = Object.entries(m.collecteddata).filter(([, v]) => renderVal(v).trim() !== "");
                    return (
                        <div key={m.target_id} className={styles.memberCard}>
                            <div className={styles.memberHeader}>
                                <div className={styles.memberIdentity}>
                                    <div className={styles.memberName}>{m.name}</div>
                                    <div className={styles.memberMeta}>
                                        {m.email && <span>{m.email}</span>}
                                        {m.phone && <span>{m.phone}</span>}
                                        {m.position && <span>{m.position}</span>}
                                    </div>
                                </div>
                                <div className={styles.memberActions}>
                                    <span className={`${styles.badge} ${m.turns.length === 0 ? styles.badgeMuted : ""}`}>
                                        {m.turns.length} message{m.turns.length === 1 ? "" : "s"}
                                    </span>
                                    <IconButton
                                        iconProps={{ iconName: "ChatInviteFriend" }}
                                        title={m.turns.length ? "Show Conversation" : "No Conversation yet"}
                                        disabled={m.turns.length === 0}
                                        onClick={() => setActiveMember(m)}
                                    />
                                </div>
                            </div>

                            {m.firstMessageAt && m.lastMessageAt && (
                                <div className={styles.timingStrip}>
                                    <div className={styles.timingItem}>
                                        <span className={styles.timingLabel}>First message</span>
                                        <span className={styles.timingValue}>{formatTurnStamp(m.firstMessageAt)}</span>
                                    </div>
                                    <div className={styles.timingItem}>
                                        <span className={styles.timingLabel}>Last message</span>
                                        <span className={styles.timingValue}>{formatTurnStamp(m.lastMessageAt)}</span>
                                    </div>
                                    <div className={styles.timingItem}>
                                        <span className={styles.timingLabel}>Period Length</span>
                                        <span className={`${styles.timingValue} ${styles.timingDuration}`}>{m.durationLabel || "0s"}</span>
                                    </div>
                                </div>
                            )}

                            {collectedEntries.length > 0 ? (
                                <div className={styles.collectedWrap}>
                                    {collectedEntries.map(([k, v]) => (
                                        <div key={k} className={styles.collectedItem}>
                                            <span className={styles.collectedKey}>{k.replace(/_/g, " ")}</span>
                                            <span className={styles.collectedVal}>{renderVal(v)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={styles.noData}>No collected data yet.</div>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* on-click conversation log modal */}
            <Modal isOpen={!!activeMember} onDismiss={() => setActiveMember(null)} isBlocking={false} containerClassName={styles.ccaConvoLogModal}>
                {activeMember && (
                    <div className={styles.modalContainer}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalTitle}>
                                <span className={styles.modalTitleName}>{activeMember.name}</span>
                                <span className={styles.modalTitleMeta}>
                                    {[activeMember.email, activeMember.phone, activeMember.position].filter(Boolean).join("  ·  ")}
                                </span>
                            </div>
                            <IconButton iconProps={{ iconName: "Cancel" }} title="Close" onClick={() => setActiveMember(null)} />
                        </div>
                        <div className={styles.modalBody}>
                            {activeMember.turns.length === 0 ? (
                                <div className={styles.modalEmpty}>No Conversation Yet</div>
                            ) : (
                                activeMember.turns.map((t, i) => {
                                    const ts = formatTurnStamp(t.stamp);
                                    return (
                                        <React.Fragment key={`${t.convo_id ?? "c"}-${t.key}-${i}`}>
                                            {t.question.trim() !== "" && (
                                                <div className={`${styles.turn} ${styles.turnUser}`}>
                                                    <div className={styles.turnHead}>
                                                        <span className={styles.turnRole}>User</span>
                                                        {ts && <span className={styles.turnTime}>{ts}</span>}
                                                    </div>
                                                    <div className={styles.turnText}>{t.question}</div>
                                                </div>
                                            )}
                                            {t.response.trim() !== "" && (
                                                <div className={`${styles.turn} ${styles.turnAssistant}`}>
                                                    <div className={styles.turnHead}>
                                                        <span className={styles.turnRole}>Kognify</span>
                                                        {ts && <span className={styles.turnTime}>{ts}</span>}
                                                    </div>
                                                    <div className={styles.turnText}>{formatText(t.response)}</div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CampaignChatMembers;
